import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { AuthLayout } from "../components/AuthLayout";
import { Field } from "../components/Field";
import type { MePublic } from "../lib/types";

interface SignupBody {
  email: string;
  password: string;
  display_name: string;
  household_name: string;
}

export function SignupPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [householdName, setHouseholdName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const nav = useNavigate();

  const mut = useMutation({
    mutationFn: (body: SignupBody) =>
      api.post<MePublic>("/api/auth/signup", body),
    onSuccess: (data) => {
      qc.setQueryData(["me"], data);
      nav("/", { replace: true });
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.detail);
      else setError("Something went wrong.");
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mut.mutate({
      email,
      password,
      display_name: displayName,
      household_name: householdName,
    });
  };

  return (
    <AuthLayout
      kicker="N° 02 — Begin"
      title={
        <>
          A new <span className="display-italic">household</span>
        </>
      }
      tagline="Found a kitchen of your own. Invite anyone else later."
    >
      <form onSubmit={onSubmit} className="space-y-7">
        <Field
          label="Household name"
          value={householdName}
          onChange={setHouseholdName}
          required
          placeholder="The Smith Kitchen"
          delay={320}
        />
        <Field
          label="Your name"
          value={displayName}
          onChange={setDisplayName}
          required
          autoComplete="name"
          delay={380}
        />
        <Field
          label="Email"
          value={email}
          onChange={setEmail}
          type="email"
          autoComplete="email"
          required
          delay={440}
        />
        <Field
          label="Password"
          value={password}
          onChange={setPassword}
          type="password"
          autoComplete="new-password"
          required
          delay={500}
        />

        {error && (
          <div className="border-l-2 border-sienna pl-4 font-body italic text-sienna animate-fade-in">
            {error}
          </div>
        )}

        <div
          className="flex flex-wrap items-center gap-8 pt-2 animate-fade-up"
          style={{ animationDelay: "580ms" }}
        >
          <button type="submit" className="btn-ink" disabled={mut.isPending}>
            {mut.isPending ? "Setting the table…" : "Begin a journal"}
          </button>
          <Link to="/login" className="label ink-link text-ink-walnut">
            Already keep one? Sign in
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
